import {
  FormControl,
  InputLabel,
  type InputProps,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  Box,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { type ReactNode } from 'react';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  selectorOptions: Array<{
    id: string;
    display: string;
    bumperId?: boolean;
    isActive?: boolean;
  }>;
  onChange: (event: SelectChangeEvent<number>, child: ReactNode) => void;
  value: any;
  selectSx?: SxProps;
  formControlSx?: SxProps;
  inputProps?: InputProps['inputProps'];
  disabled?: boolean;
  fallbackValue?: string;
  minWidth?: string;
  enableDarkTheme?: boolean;
  height?: string;
  minHeight?: string;
  label?: string;
  fontSize?: string;
  size?: 'small' | 'medium';
}

export const Selector = ({
  selectorOptions,
  onChange,
  value,
  selectSx,
  formControlSx,
  inputProps,
  disabled = false,
  fallbackValue = '',
  minWidth = '270px',
  enableDarkTheme = false,
  height,
  minHeight,
  fontSize,
  label,
  size,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <FormControl sx={{ minWidth, ...formControlSx }} size="small">
      {/* updated to support labels as well  */}
      {label && (
        <InputLabel
          id={`select-label-${label}`}
          sx={{
            color: palette.textPrimary,
            '&.Mui-focused': {
              color: palette.textPrimary,
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        labelId={`select-label-${label}`}
        label={label}
        size={size ?? 'small'}
        value={value ?? ''}
        onChange={onChange}
        SelectDisplayProps={{
          style: {
            display: 'flex',
            height: height ?? '12px',
            minHeight: minHeight ?? '12px',
            alignItems: 'center',
            fontSize: fontSize ?? '12px',
          },
        }}
        inputProps={inputProps}
        sx={{
          ...selectSx,
          color: enableDarkTheme ? palette.textSecondary : palette.textPrimary,
          '& .MuiSvgIcon-root': {
            color: palette.primary,
          },
          '&.MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: enableDarkTheme
                ? palette.borderGrey
                : palette.border,
            },
            '&:hover fieldset': {
              borderColor: palette.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.primary,
            },
          },
        }}
        disabled={disabled}
        displayEmpty={!!fallbackValue}
        MenuProps={{
          PaperProps: {
            style: {
              backgroundColor: palette.menuOptions,
            },
          },
        }}
      >
        <MenuItem
          value=""
          disabled
          sx={{
            color: palette.textSecondary,
          }}
        >
          {fallbackValue}
        </MenuItem>
        {selectorOptions.map(({ id, display, bumperId, isActive }) => (
          <MenuItem
            key={id}
            value={id}
            sx={{
              color: palette.textSecondary,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {bumperId && (
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: isActive ? '#199161' : '#fc0000',
                    marginRight: '5px',
                  }}
                />
              )}
              {display}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
